import {
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  InputAdornment,
  ListSubheader,
  FormControl,
  Dialog
} from '@mui/material';
import Multiselect from 'multiselect-react-dropdown';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import SearchIcon from '@mui/icons-material/Search';
import { updateTeamResources } from 'src/services/projectService';
import { AppActions } from 'src/store/AppReducer';
import ConfirmationModal from 'src/content/common/confirmationModal/ConfirmationModal';
import moment from 'moment';
import { useAppSelector } from 'src/hooks/hooks';
import { DatePicker } from '@mui/lab';
import 'src/content/projectDetails/ProjectDetails.css';
import { getCalendarMonthInRange } from './calendarMonth';
import {
  getAllBillingRoles,
  getAllEmployees,
  getAllSkills,
  getAllTracks
} from 'src/services/employeeService';
import { createTeam, createTeamResource } from 'src/services/projectService';

const CreateForm = ({
  open,
  toggle,
  getData,
  teams,
  projectCode,
  projectDetails,
  setSnackBarOpen,
  setSnackbarSeverity,
  setSnackBarMessage
}) => {
  const dispatch = useDispatch();
  const { isLoading } = useAppSelector((state) => state.appReducer);

  const [createRoleObj, setCreateRoleObj] = useState<any>({});
  const [allocateResource, setAllocateResource] = useState<any>({});
  const [createRoleObjErrors, setCreateRoleObjErrors] = useState<any>({});
  const [searchText, setSearchText] = useState('');
  const [searchText2, setSearchText2] = useState('');
  const [allSkills, setAllSkills] = useState<any>([]);
  const [billingRoles, setBillingRoles] = useState<any>([]);
  const [tracks, setTracks] = useState<any>([]);
  const [allEmployees, setAllEmployees] = useState<any>([]);
  const [selectResource, setSelectResource] = useState<any>('');
  const [resetKey, setResetKey] = useState(0);
  const [isCreate, setIsCreate] = useState(true);

  const [selectedResourceInfo, setSelectedResourceInfo] = useState<any>({});

  const getAllListData = () => {
    getAllEmployees().then((res) => setAllEmployees(res.data));
    getAllBillingRoles().then((res) => setBillingRoles(res.data));
    getAllTracks().then((res) => setTracks(res.data));
    getAllSkills().then((res) => {
      setAllSkills(
        res?.data.map((ele) => {
          return { name: ele };
        })
      );
    });
  };

  useEffect(() => {
    getAllListData();
  }, []);

  const onSelect = (selectedList, selectedItem) => {
    setCreateRoleObj((prev) => ({
      ...prev,
      skills: selectedList.map((sel) => sel.name)
    }));
  };

  const onRemove = (selectedList, selectedItem) => {
    setCreateRoleObj((prev) => ({
      ...prev,
      skills: selectedList.map((sel) => sel.name)
    }));
  };

  const containsText = (text, searchText) =>
    text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

  const displayedOptions = useMemo(
    () =>
      billingRoles.length > 0 &&
      billingRoles?.filter((option) => containsText(option.name, searchText)),
    [searchText, billingRoles]
  );

  const displayedOptions2 = useMemo(
    () =>
      allEmployees.length > 0 &&
      allEmployees?.filter((option) => containsText(option.name, searchText2) && !option?.employeeFinance?.isResign),
    [searchText2, allEmployees]
  );

  useEffect(() => {
    setDefaultValues();
    setIsCreate(false);
  }, [teams, projectCode, projectDetails, isCreate]);

  const padLeft = (nr?: any, n?: any, str?: any) => {
    return Array(n - String(nr).length + 1).join(str || '0') + nr;
  };

  const setDefaultValues = () => {
    if (projectDetails?.key) {
      setCreateRoleObj((prev) => ({
        ...prev,
        projectCode: projectDetails?.key
      }));
    }

    if (teams.length === 0) {
      setCreateRoleObj((prev) => ({
        ...prev,
        code: `${projectDetails?.key}001`
      }));
      setAllocateResource((prev) => ({
        ...prev,
        projectRoleCode: `${projectDetails?.key}001`
      }));
    } else {
      const code = teams[0]?.code;

      const splitCode = code.split(projectDetails?.key)[1];
      setCreateRoleObj((prev) => ({
        ...prev,
        code: `${projectDetails?.key}${padLeft(Number(splitCode) + 1, 3)}`
      }));
      setAllocateResource((prev) => ({
        ...prev,
        projectRoleCode: `${projectDetails?.key}${padLeft(
          Number(splitCode) + 1,
          3
        )}`
      }));
    }
  };

  const [openConfirmation, setOpenConfirmation] = useState(false);

  const toggleConfirmation = () => {
    const errorsObj: any = {};

    if (!createRoleObj.role) {
      errorsObj.roleErrors = 'Project role is required.';
    }
    if (!createRoleObj.startDate) {
      errorsObj.startDateError = 'Start date is required.';
    }
    if (!createRoleObj.endDate) {
      errorsObj.endDateError = 'End date is required.';
    } else if (
      Date.parse(createRoleObj.startDate) > Date.parse(createRoleObj.endDate)
    ) {
      errorsObj.endDateError = 'End date should be greater than start date';
    }
    if (!createRoleObj.allocation) {
      errorsObj.allocationError = 'Allocation is required.';
    }
    if (!createRoleObj.type) {
      errorsObj.typeError = 'Role Type is required.';
    }
    if (!createRoleObj.trackCode) {
      errorsObj.trackCodeError = 'Track Code is required.';
    }
    if (allocateResource.name && !allocateResource.type) {
      errorsObj.empTypeError = 'Resource Type is required.';
    }
    if (allocateResource.name && !allocateResource.allocation) {
      errorsObj.empAllocationError = 'Resource allocation is required.';
    }
    if (allocateResource.name && !allocateResource.startDate) {
      errorsObj.empStartDateError = 'Resource start date is required.';
    }
    if (allocateResource.name && !allocateResource.endDate) {
      errorsObj.empEndDateError = 'Resource end date is required.';
    } else if (
      Date.parse(allocateResource.startDate) >
      Date.parse(allocateResource.endDate)
    ) {
      errorsObj.empEndDateError = 'End date should be greater than start date';
    } else if (createRoleObj.allocation) {
      if (allocateResource.allocation > createRoleObj.allocation) {
        errorsObj.empAllocationError =
          'Resource Allocation should be equal or less than to the Role Allocation percentage.';
      }
    }

    setCreateRoleObjErrors(errorsObj);
    if (Object.keys(errorsObj).length === 0) {
      setOpenConfirmation(!openConfirmation);
    }
  };

  const onClickCreate = () => {
    dispatch(AppActions.showLoader());

    createTeam(createRoleObj).then(() => {
      if (!allocateResource.name) {
        setSnackBarMessage('You have added new role successfully.');
        setSnackbarSeverity('success');
        setSnackBarOpen(true);
        setSearchText('');
        setSearchText2('');
        getData();
        toggleConfirmation();
        setCreateRoleObj({});
        setAllocateResource({});
        setSelectResource('');
        setResetKey((prevKey) => prevKey + 1);
        setIsCreate(true);
        toggle();
        dispatch(AppActions.hideLoader());
      } else {
        createTeamResource(allocateResource).then(() => {
          setSnackBarMessage('You have added new role successfully.');
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          setSearchText('');
          setSearchText2('');
          getData();
          toggleConfirmation();
          setCreateRoleObj({});
          setAllocateResource({});
          setSelectResource('');
          setResetKey((prevKey) => prevKey + 1);
          setIsCreate(true);
          toggle();
          dispatch(AppActions.hideLoader());
        })
        .catch((error) => {
          toggleConfirmation();
          setSnackBarMessage(error?.response?.data ? error?.response?.data : 'Unable to add the Project Role.');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
          setSearchText('');
          setSearchText2('');
          getData();
          toggleConfirmation();
          setCreateRoleObj({});
          setAllocateResource({});
          setSelectResource('');
          setResetKey((prevKey) => prevKey + 1);
          setIsCreate(true);
          toggle();
          dispatch(AppActions.hideLoader());
        });
      }
    }).catch((error) => {
      setSnackBarMessage(error?.response?.data ? error?.response?.data : 'Unable to add the Project Role.');
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
      setSearchText('');
      setSearchText2('');
      getData();
      toggleConfirmation();
      setCreateRoleObj({});
      setAllocateResource({});
      setSelectResource('');
      setResetKey((prevKey) => prevKey + 1);
      setIsCreate(true);
      toggle();
      dispatch(AppActions.hideLoader());
    })
  };

  const onClosePopUp = () => {
    setCreateRoleObj({});
    setAllocateResource({});
    setSelectResource('');
    setSearchText('');
    setSearchText2('');
    setCreateRoleObjErrors({});
    setResetKey((prevKey) => prevKey + 1);
    setIsCreate(true);
    toggle();
  };

  //set track code
  useEffect(() => {
    billingRoles?.filter((item) => {
      if (item.id === createRoleObj.roleId) {
        if (
          item.name === 'Senior Project Manager' ||
          item.name === 'Project Manager' ||
          item.name === 'Associate Project Manager' ||
          item.name === 'Intern Project Manager' ||
          item.name === 'Project Coordinator' ||
          item.name === 'Team Lead' ||
          item.name === 'Associate Team Lead' ||
          item.name === 'Senior Team Lead' ||
          item.name === 'Senior Team Lead/Scrum Master' ||
          item.name === 'Technical Project Manager' ||
          item.name === 'Head Of Delivery'
        ) {
          setCreateRoleObj((prev) => ({
            ...prev,
            trackCode: 'PM'
          }));
        } else if (
          item.name === 'Quality Assurance Manager' ||
          item.name === 'Quality Assurance Lead' ||
          item.name === 'Senior QA Engineer' ||
          item.name === 'QA Engineer' ||
          item.name === 'Associate QA Engineer' ||
          item.name === 'Intern QA Engineer' ||
          item.name === 'Associate QA Lead'
        ) {
          setCreateRoleObj((prev) => ({
            ...prev,
            trackCode: 'QA'
          }));
        } else if (
          item.name === 'Lead Business Analyst ' ||
          item.name === 'Associate Lead Business Analyst' ||
          item.name === 'Senior Business Analyst' ||
          item.name === 'Business Analyst' ||
          item.name === 'Associate Business Analyst' ||
          item.name === 'Intern Business Analyst' ||
          item.name === 'Business Consulatant'
        ) {
          setCreateRoleObj((prev) => ({
            ...prev,
            trackCode: 'BA'
          }));
        } else if (
          item.name === 'Management Trainee' ||
          item.name === 'Intern Finance & Administration' ||
          item.name === 'Manager Finance & Administration' ||
          item.name === 'Administrative Assistant' ||
          item.name === 'Assistant Accountant' ||
          item.name === 'System Adminstrator' ||
          item.name === 'Trainee Accountant' ||
          item.name === 'Assistant System Administrator'
        ) {
          setCreateRoleObj((prev) => ({
            ...prev,
            trackCode: 'Admin'
          }));
        } else if (
          item.name === 'Senior HR Executive' ||
          item.name === 'HR Executive' ||
          item.name === 'Manager Operations' ||
          item.name === 'Manager HR' ||
          item.name === 'Assistant Manager HR' ||
          item.name === 'Chief Operating Officer' ||
          item.name === 'Managing Director' ||
          item.name === 'Chief Executive Officer' ||
          item.name === 'Manager Business Development'
        ) {
          setCreateRoleObj((prev) => ({
            ...prev,
            trackCode: 'Ops'
          }));
        } else if (item.name === 'Not Applicable') {
          setCreateRoleObj((prev) => ({
            ...prev,
            trackCode: 'NA'
          }));
        } else {
          setCreateRoleObj((prev) => ({
            ...prev,
            trackCode: 'Dev'
          }));
        }
      }
    });
  }, [createRoleObj.roleId]);

  const LabelStyle = {
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px'
  };

  const TextFieldStyle = {
    paddingTop: 5,
    paddingLeft: 15,
    paddingRight: 11,
    fontSize: '13px',
    width: '100%'
  };

  const selectStyle = {
    width: '90%',
    marginLeft: 15,
    marginTop: 5,
    paddingRight: 15,
    fontSize: '13px'
  };

  return (
    <Dialog
      open={open}
      keepMounted
      maxWidth="md"
      fullWidth
      scroll="paper"
      onClose={toggle}
    >
      <Grid sx={{ p: 2, marginTop: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Project Role<span style={{ color: 'red' }}>*</span>
            </Typography>
            <Select
              MenuProps={{ autoFocus: false }}
              labelId="search-select-label"
              style={{
                height: '2.25rem',
                width: '94%',
                marginLeft: 15,
                marginTop: 5,
                paddingRight: 15,
                fontSize: '13px'
              }}
              id="create-roleId"
              displayEmpty
              value={createRoleObj.roleId || ''}
              onChange={(e) => {
                const filteredRole = billingRoles.filter(
                  (fil) => e.target.value === fil.id
                )[0];
                setCreateRoleObj((prev) => ({
                  ...prev,
                  role: filteredRole?.name
                }));
                setCreateRoleObj((prev) => ({
                  ...prev,
                  roleId: e.target.value
                }));
                setCreateRoleObjErrors((prev) => ({
                  ...prev,
                  roleErrors: '',
                  trackCodeError: ''
                }));
              }}
            >
              <ListSubheader>
                <TextField
                  id="search-roleId"
                  size="small"
                  // Autofocus on textfield
                  autoFocus
                  placeholder="Type to search..."
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') {
                      // Prevents autoselecting item while typing (default Select behaviour)
                      e.stopPropagation();
                    }
                  }}
                />
              </ListSubheader>
              <MenuItem
                style={{ display: 'none' }}
                selected
                disabled
                value={''}
              >
                Project Role
              </MenuItem>
              {displayedOptions.length > 0 &&
                displayedOptions?.map((option, i) => (
                  <MenuItem key={i} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
            </Select>
            {createRoleObjErrors.roleErrors && (
              <Typography
                style={{
                  fontSize: '9px',
                  textTransform: 'capitalize',
                  marginLeft: 15
                }}
                color="red"
              >
                {createRoleObjErrors.roleErrors}
              </Typography>
            )}
          </Grid>
          <Grid item xs={4}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Start Date<span style={{ color: 'red' }}>*</span>
            </Typography>
            <div style={{ marginTop: 5, marginLeft: 10, marginRight: 10 }}>
              <FormControl fullWidth>
                <DatePicker
                  minDate={projectDetails?.type !== "Pre-sale" ? 
                    ((projectDetails?.actualStartDate !== null && projectDetails?.actualStartDate !== '0001-01-01T00:00:00')) ? new Date(projectDetails?.actualStartDate) :
                    ((projectDetails?.actualStartDate === null || projectDetails?.actualStartDate === '0001-01-01T00:00:00') && 
                    (projectDetails?.plannedStartDate !== null && projectDetails?.plannedStartDate !== '0001-01-01T00:00:00')) && new Date(projectDetails?.plannedStartDate)
                    : null
                  }
                  maxDate={projectDetails?.type !== "Pre-sale" ? (projectDetails?.actualEndDate !== null && projectDetails?.actualEndDate !== '0001-01-01T00:00:00') ? new Date(projectDetails?.actualEndDate) :
                    ((projectDetails?.actualEndDate === null || projectDetails?.actualEndDate === '0001-01-01T00:00:00')) && null
                     : null
                  }
                  InputProps={{
                    className: 'date-picker'
                  }}
                  renderInput={(params) => (
                    <TextField {...params} id="create-startDate" />
                  )}
                  key={createRoleObj.startDate}
                  value={createRoleObj.startDate || ''}
                  onChange={(newValue) => {
                    if (newValue) {
                      setCreateRoleObj((prev) => ({
                        ...prev,
                        startDate: moment(newValue).format('YYYY-MM-DD')
                      }));
                      setCreateRoleObjErrors((prev) => ({
                        ...prev,
                        startDateError: ''
                      }));
                    }
                  }}
                />
              </FormControl>
            </div>
            {createRoleObjErrors.startDateError && (
              <Typography
                style={{
                  fontSize: '9px',
                  textTransform: 'capitalize',
                  marginLeft: 15
                }}
                color="red"
              >
                {createRoleObjErrors.startDateError}
              </Typography>
            )}
          </Grid>
          <Grid item xs={4}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              End Date<span style={{ color: 'red' }}>*</span>
            </Typography>
            <div style={{ marginTop: 5, marginLeft: 10, marginRight: 10 }}>
              <FormControl fullWidth>
                <DatePicker
                  InputProps={{
                    className: 'date-picker'
                  }}
                  disabled={!createRoleObj.startDate}
                  minDate={new Date(createRoleObj.startDate)}
                  maxDate={projectDetails?.type !== "Pre-sale" ? (projectDetails?.actualEndDate !== null && projectDetails?.actualEndDate !== '0001-01-01T00:00:00') ? new Date(projectDetails?.actualEndDate) :
                    ((projectDetails?.actualEndDate === null || projectDetails?.actualEndDate === '0001-01-01T00:00:00')) && null : 
                    null
                  }
                  renderInput={(params) => (
                    <TextField {...params} id="create-endDate" />
                  )}
                  key={createRoleObj.endDate}
                  value={createRoleObj.endDate || ''}
                  onChange={(newValue) => {
                    if (newValue) {
                      setCreateRoleObj((prev) => ({
                        ...prev,
                        endDate: moment(newValue).format('YYYY-MM-DD')
                      }));
                      setCreateRoleObjErrors((prev) => ({
                        ...prev,
                        endDateError: ''
                      }));
                    }
                  }}
                />
              </FormControl>
            </div>
            {createRoleObjErrors.endDateError && (
              <Typography
                style={{
                  fontSize: '9px',
                  textTransform: 'capitalize',
                  marginLeft: 15
                }}
                color="red"
              >
                {createRoleObjErrors.endDateError}
              </Typography>
            )}
          </Grid>
          <Grid item xs={4}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Allocation<span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              id="create-allocation"
              className="input-text"
              size="small"
              variant="outlined"
              style={TextFieldStyle}
              inputProps={{
                max: 100,
                min: 0,
                style: {
                  fontSize: '13px'
                }
              }}
              type="number"
              placeholder="Allocation(%)"
              value={createRoleObj.allocation || ''}
              onChange={(e) => {
                let value = parseInt(e.target.value, 10);
                if (value > 100) value = 100;

                setCreateRoleObj((prev) => ({
                  ...prev,
                  allocation: value
                }));
                setCreateRoleObjErrors((prev) => ({
                  ...prev,
                  allocationError: ''
                }));
              }}
            />
            {createRoleObjErrors.allocationError && (
              <Typography
                style={{
                  fontSize: '9px',
                  textTransform: 'capitalize',
                  marginLeft: 15
                }}
                color="red"
              >
                {createRoleObjErrors.allocationError}
              </Typography>
            )}
          </Grid>
          <Grid item xs={4}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Role Type<span style={{ color: 'red' }}>*</span>
            </Typography>
            <Select
              size="small"
              id="create-type"
              displayEmpty
              inputProps={{ 'aria-label': 'Without label' }}
              style={selectStyle}
              value={createRoleObj.type || ''}
              onChange={(e) => {
                setCreateRoleObj((prev) => ({
                  ...prev,
                  type: e.target.value
                }));
                setCreateRoleObjErrors((prev) => ({
                  ...prev,
                  typeError: ''
                }));
              }}
            >
              <MenuItem
                style={{ display: 'none' }}
                selected
                disabled
                value={''}
              >
                Role Type
              </MenuItem>
              <MenuItem value={`Planned`}>Planned</MenuItem>
              <MenuItem value={`Unplanned`}>Unplanned</MenuItem>
            </Select>
            {createRoleObjErrors.typeError && (
              <Typography
                style={{
                  fontSize: '9px',
                  textTransform: 'capitalize',
                  marginLeft: 15
                }}
                color="red"
              >
                {createRoleObjErrors.typeError}
              </Typography>
            )}
          </Grid>
          <Grid item xs={4}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Track Code<span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              id="create-trackCode"
              size="small"
              disabled
              inputProps={{
                style: {
                  fontSize: '13px'
                }
              }}
              placeholder="Track Code"
              style={TextFieldStyle}
              value={createRoleObj.trackCode || ''}
              onChange={(e) => {}}
            ></TextField>
            {createRoleObjErrors.trackCodeError && (
              <Typography
                style={{
                  fontSize: '9px',
                  textTransform: 'capitalize',
                  marginLeft: 15
                }}
                color="red"
              >
                {createRoleObjErrors.trackCodeError}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Skills
            </Typography>
            <Multiselect
              style={{
                multiselectContainer: {
                  paddingLeft: 15,
                  paddingRight: 15,
                  width: '33.3%'
                }
              }}
              id="create-onSelect"
              key={resetKey}
              placeholder={'Select Skills'}
              options={allSkills}
              onSelect={onSelect}
              onRemove={onRemove}
              displayValue="name"
            />
            {createRoleObjErrors.skillError && (
              <Typography
                style={{
                  fontSize: '9px',
                  textTransform: 'capitalize'
                }}
                color="red"
              >
                {createRoleObjErrors.skillError}
              </Typography>
            )}
          </Grid>
          <Grid item xs={4} sx={{ mt: 2 }}>
            <Typography
              style={LabelStyle}
              variant="h4"
              component="h4"
              fontWeight="light"
            >
              Resource Name
            </Typography>
            <Select
              id="create-selectResource"
              MenuProps={{ autoFocus: false }}
              labelId="search-select-label"
              style={{
                height: '2.25rem',
                width: '94%',
                marginLeft: 15,
                marginTop: 5,
                paddingRight: 15,
                fontSize: '13px'
              }}
              displayEmpty
              value={selectResource || ''}
              onChange={(e) => {
                setSelectResource(e.target.value);
                const id = e.target.value;
                const filteredEmp = allEmployees?.filter(
                  (emp) => emp?.employeeId === id
                )[0];
                setSelectedResourceInfo(filteredEmp);

                setAllocateResource((prev) => ({
                  ...prev,
                  employeeId: e.target.value
                }));
                setAllocateResource((prev) => ({
                  ...prev,
                  name: filteredEmp?.name
                }));
                setCreateRoleObjErrors((prev) => ({
                  ...prev,
                  empError: ''
                }));
              }}
            >
              <ListSubheader>
                <TextField
                  id="search-selectResource"
                  size="small"
                  autoFocus
                  placeholder="Type to search..."
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                  onChange={(e) => {
                    setSearchText2(e.target.value)
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') {
                      // Prevents autoselecting item while typing (default Select behaviour)
                      e.stopPropagation();
                    }
                  }}
                />
              </ListSubheader>
              <MenuItem
                style={{ display: 'none' }}
                selected
                disabled
                value={''}
              >
                Resource Name
              </MenuItem>
              {displayedOptions2.length > 0 &&
                displayedOptions2?.map((option, i) => (
                  <MenuItem key={i} value={option.employeeId}>
                    {option.name}
                  </MenuItem>
                ))}
            </Select>
            {createRoleObjErrors.empError && (
              <Typography
                style={{
                  fontSize: '9px',
                  textTransform: 'capitalize',
                  marginLeft: 15
                }}
                color="red"
              >
                {createRoleObjErrors.empError}
              </Typography>
            )}
          </Grid>
          <Grid item xs={4} sx={{ mt: 2 }}>
            <Typography
              style={LabelStyle}
              variant="h4"
              component="h4"
              fontWeight="light"
            >
              Resource Type
            </Typography>
            <Select
              id="create-allocateResourceType"
              disabled={!allocateResource.name}
              size="small"
              displayEmpty
              // defaultValue={allocateResource.resourceType}
              inputProps={{ 'aria-label': 'Without label' }}
              style={selectStyle}
              value={allocateResource.type || ''}
              onChange={(e) => {
                setAllocateResource((prev) => ({
                  ...prev,
                  type: e.target.value
                }));
                setCreateRoleObjErrors((prev) => ({
                  ...prev,
                  empTypeError: ''
                }));
              }}
            >
              <MenuItem
                style={{ display: 'none' }}
                selected
                disabled
                value={''}
              >
                Resource Type
              </MenuItem>
              <MenuItem value="Billable">Billable</MenuItem>
              <MenuItem value="Non-Billable">Non-Billable</MenuItem>
              <MenuItem value="Shadow">Shadow</MenuItem>
              <MenuItem value="Support">Support</MenuItem>
              <MenuItem value="T&M">T&M</MenuItem>
            </Select>
            {createRoleObjErrors.empTypeError && (
              <Typography
                style={{
                  fontSize: '9px',
                  textTransform: 'capitalize',
                  marginLeft: 15
                }}
                color="red"
              >
                {createRoleObjErrors.empTypeError}
              </Typography>
            )}
          </Grid>
          <Grid item xs={4} sx={{ mt: 2 }}>
            <Typography
              style={LabelStyle}
              variant="h4"
              component="h4"
              fontWeight="light"
            >
              Resource Allocation
            </Typography>
            <TextField
              id="create-allocateResourceAllocation"
              disabled={!createRoleObj.allocation}
              size="small"
              variant="outlined"
              style={TextFieldStyle}
              inputProps={{
                max: 100,
                min: 0,
                style: {
                  fontSize: '11px'
                }
              }}
              placeholder="Resource Allocation(%)"
              type="number"
              value={allocateResource.allocation || ''}
              onChange={(e) => {
                let value = parseInt(e.target.value, 10);
                if (value > createRoleObj.allocation) {
                  setCreateRoleObjErrors((prev) => ({
                    ...prev,
                    empAllocationError:
                      'Resource Allocation should be equal or less than to the Role Allocation percentage.'
                  }));
                  setAllocateResource((prev) => ({
                    ...prev,
                    allocation: value
                  }));
                } else {
                  setAllocateResource((prev) => ({
                    ...prev,
                    allocation: value
                  }));
                  setCreateRoleObjErrors((prev) => ({
                    ...prev,
                    empAllocationError: ''
                  }));
                }
              }}
            />
            {createRoleObjErrors.empAllocationError && (
              <Typography
                style={{
                  fontSize: '9px',
                  textTransform: 'capitalize',
                  marginLeft: 15
                }}
                color="red"
              >
                {createRoleObjErrors.empAllocationError}
              </Typography>
            )}
          </Grid>
          <Grid item xs={4} sx={{ mt: 2 }}>
            <Typography
              style={LabelStyle}
              variant="h4"
              component="h4"
              fontWeight="light"
            >
              Start Date
            </Typography>
            <div style={{ marginTop: 5, marginLeft: 10, marginRight: 10 }}>
              <FormControl fullWidth>
                <DatePicker
                  disabled={!allocateResource.name}
                  InputProps={{
                    className: 'date-picker'
                  }}
                  defaultCalendarMonth={getCalendarMonthInRange(
                    createRoleObj?.startDate,
                    createRoleObj?.endDate
                  )}
                  minDate={new Date(createRoleObj.startDate) > new Date(selectedResourceInfo?.joinedDate) ? new Date(createRoleObj.startDate) : new Date(selectedResourceInfo?.joinedDate)}
                  maxDate={selectedResourceInfo?.employeeFinance?.lastWorkingDate ? 
                    new Date(createRoleObj.endDate) > new Date(selectedResourceInfo?.employeeFinance?.lastWorkingDate) ? 
                    new Date(selectedResourceInfo?.employeeFinance?.lastWorkingDate) : new Date(createRoleObj.endDate)
                    : new Date(createRoleObj.endDate)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="create-allocateResourceStartDate"
                    />
                  )}
                  key={allocateResource.startDate}
                  value={allocateResource.startDate || ''}
                  onChange={(newValue) => {
                    if (newValue) {
                      setAllocateResource((prev) => ({
                        ...prev,
                        startDate: moment(newValue).format('YYYY-MM-DD')
                      }));
                      setCreateRoleObjErrors((prev) => ({
                        ...prev,
                        empStartDateError: ''
                      }));
                    }
                  }}
                />
              </FormControl>
            </div>
            {createRoleObjErrors.empStartDateError && (
              <Typography
                style={{
                  fontSize: '9px',
                  textTransform: 'capitalize',
                  marginLeft: 15
                }}
                color="red"
              >
                {createRoleObjErrors.empStartDateError}
              </Typography>
            )}
          </Grid>
          <Grid item xs={4} sx={{ mt: 2 }}>
            <Typography
              style={LabelStyle}
              variant="h4"
              component="h4"
              fontWeight="light"
            >
              End Date
            </Typography>
            <div style={{ marginTop: 5, marginLeft: 10, marginRight: 10 }}>
              <FormControl fullWidth>
                <DatePicker
                  disabled={!allocateResource.startDate}
                  InputProps={{
                    className: 'date-picker'
                  }}
                  minDate={new Date(allocateResource.startDate) ? new Date(allocateResource.startDate) : 
                    new Date(createRoleObj.startDate) > new Date(selectedResourceInfo?.joinedDate) ? 
                    new Date(createRoleObj.startDate) : new Date(selectedResourceInfo?.joinedDate)}
                  maxDate={selectedResourceInfo?.employeeFinance?.lastWorkingDate ? 
                    new Date(createRoleObj.endDate) > new Date(selectedResourceInfo?.employeeFinance?.lastWorkingDate) ? 
                    new Date(selectedResourceInfo?.employeeFinance?.lastWorkingDate) : new Date(createRoleObj.endDate)
                    : new Date(createRoleObj.endDate)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="create-allocateResourceEndDate"
                    />
                  )}
                  key={allocateResource.endDate}
                  value={allocateResource.endDate || ''}
                  onChange={(newValue) => {
                    if (newValue) {
                      setAllocateResource((prev) => ({
                        ...prev,
                        endDate: moment(newValue).format('YYYY-MM-DD')
                      }));
                      setCreateRoleObjErrors((prev) => ({
                        ...prev,
                        empEndDateError: ''
                      }));
                    }
                  }}
                />
              </FormControl>
            </div>
            {createRoleObjErrors.empEndDateError && (
              <Typography
                style={{
                  fontSize: '9px',
                  textTransform: 'capitalize',
                  marginLeft: 15
                }}
                color="red"
              >
                {createRoleObjErrors.empEndDateError}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Grid
          item
          sx={{
            mt: 3,
            pr: 2,
            display: 'flex',
            justifyContent: 'flex-end'
          }}
          xs={12}
        >
          <Button
            id="btn-create-setClickedCreate"
            sx={{
              width: '10%',
              fontWeight: 'normal',
              background: '#F5F7FF',
              fontSize: '12px'
            }}
            onClick={() => onClosePopUp()}
          >
            Cancel
          </Button>
          <Button
            id="btn-create-toggleConfirmation"
            onClick={() => {
              toggleConfirmation();
            }}
            sx={{
              ml: 2,
              width: '10%',
              fontSize: '12px',
              fontWeight: 'normal',
              background: '#0069E4'
            }}
            variant="contained"
            type="button"
          >
            Add
          </Button>
        </Grid>
        <ConfirmationModal
          isLoading={isLoading}
          open={openConfirmation}
          toggle={toggleConfirmation}
          message={`Are you sure you want to add a new role?`}
          onConfirm={() => onClickCreate()}
          noToggle={toggleConfirmation}
        />
      </Grid>
    </Dialog>
  );
};

export default CreateForm;
